import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Phone, Sparkles } from "lucide-react";
import { toast } from "../hooks/use-toast";

const BookingContext = createContext({ open: () => {} });

export const useBooking = () => useContext(BookingContext);

const DURATIONS = [
  "30 minutes",
  "45 minutes",
  "1 hour",
  "75 minutes",
  "1.5 hours",
  "2 hours",
  "3 hours",
  "4 hours",
  "5 hours",
  "6 hours",
  "Escort / Outcall",
];

export const BookingProvider = ({ children, venueNames = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState({ venue: "", duration: "" });
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });

  const open = useCallback((data = {}) => {
    setPrefill({ venue: data.venue || "", duration: data.duration || "" });
    setIsOpen(true);
  }, []);

  const value = useMemo(() => ({ open }), [open]);

  const submit = (e) => {
    e.preventDefault();
    const record = { ...form, ...prefill, createdAt: new Date().toISOString() };
    const existing = JSON.parse(localStorage.getItem("sbg_bookings") || "[]");
    localStorage.setItem("sbg_bookings", JSON.stringify([record, ...existing]));
    toast({
      title: "Enquiry received",
      description: `Thanks ${form.name || ""}, reception will confirm your ${
        prefill.duration || "booking"
      } shortly.`,
    });
    setForm({ name: "", phone: "", email: "", notes: "" });
    setIsOpen(false);
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[520px] bg-popover border-border/60 p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent px-6 pt-6 pb-4">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/20">
                  <Sparkles className="h-4 w-4 text-primary" />
                </span>
                <DialogTitle className="font-serif text-2xl text-foreground">
                  Make an Enquiry
                </DialogTitle>
              </div>
            </DialogHeader>
            <p className="mt-2 text-sm text-muted-foreground">
              Walk-ins are always welcome. Send a note ahead and reception will hold your preferred time.
            </p>
          </div>

          <form className="space-y-4 px-6 pb-6 pt-2" onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bk-name">Your name *</Label>
                <Input
                  id="bk-name"
                  required
                  placeholder="John Smith"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bk-phone">Phone *</Label>
                <Input
                  id="bk-phone"
                  required
                  type="tel"
                  placeholder="0400 000 000"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bk-email">Email</Label>
              <Input
                id="bk-email"
                type="email"
                placeholder="you@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Venue</Label>
                <Select
                  value={prefill.venue}
                  onValueChange={(v) => setPrefill((p) => ({ ...p, venue: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a venue" />
                  </SelectTrigger>
                  <SelectContent>
                    {venueNames.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={prefill.duration}
                  onValueChange={(v) => setPrefill((p) => ({ ...p, duration: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bk-notes">Anything we should know?</Label>
              <Textarea
                id="bk-notes"
                rows={3}
                className="resize-none"
                placeholder="Preferred time, room type, couples booking, accessibility needs..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <button type="submit" className="btn-pink-glow w-full py-3 text-base">
              Submit Enquiry
            </button>

            <a
              href="tel:+61292124455"
              className="flex items-center justify-center gap-2 rounded-md border-2 border-primary/40 py-3 text-sm text-foreground transition-colors hover:bg-primary/10"
            >
              <Phone className="h-4 w-4 text-primary" />
              Prefer to call? Reception is open now
            </a>
          </form>
        </DialogContent>
      </Dialog>
    </BookingContext.Provider>
  );
};

export default BookingProvider;
